import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  Typography,
  useTheme,
  CircularProgress,
  Box,
  Paper,
  Avatar,
} from "@mui/material";
import { Close as CloseIcon, Schedule as ScheduleIcon, RestaurantMenu } from "@mui/icons-material";
import React, { useState } from "react";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { gql, useMutation } from "@apollo/client";
import * as Yup from "yup";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// GraphQL mutation remains the same
const SCHEDULE_COOKING_MUTATION = gql`
  mutation ScheduleCooking(
    $scheduled_date: String!
    $cook_in: String!
    $tasks: String!
    $recipe_name: String!
    $recipe_id: ID!
  ) {
    scheduleCooking(
      scheduled_date: $scheduled_date
      cook_in: $cook_in
      tasks: $tasks
      recipe_name: $recipe_name
      recipe_id: $recipe_id
    ) {
      message
      success
    }
  }
`;

export default function CookLaterDialog({
  openScheduleModal,
  handleScheduleClose,
  scheduledDate,
  setScheduledDate,
  cook_in,
  tasks,
  recipe_id,
  recipe_name,
  code,
}) {
  const theme = useTheme();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [scheduleCooking] = useMutation(SCHEDULE_COOKING_MUTATION, {
    onCompleted: (data) => {
      setIsSubmitting(false);
      if (data.scheduleCooking.success) {
        toast.success("Cooking time scheduled successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
        setTimeout(handleScheduleClose, 3000);
      } else {
        toast.error(
          data.scheduleCooking.message || "Failed to schedule cooking.",
          { position: "top-center", autoClose: 3000 }
        );
      }
    },
    onError: (error) => {
      setIsSubmitting(false);
      toast.error(`Error: ${error.message}`, {
        position: "top-center",
        autoClose: 3000,
      });
    },
  });

  const validationSchema = Yup.object().shape({
    scheduledDate: Yup.date()
      .min(
        new Date(new Date().getTime() + 2 * 60 * 1000),
        "Date and time should be at least 2 minutes from now."
      )
      .required("Please select a valid date and time."),
  });

  const handleScheduleSave = async () => {
    try {
      await validationSchema.validate({ scheduledDate });
      setIsSubmitting(true);
      scheduleCooking({
        variables: {
          scheduled_date: scheduledDate?.toISOString(),
          cook_in,
          tasks: JSON.stringify({ tasks, code }),
          recipe_id,
          recipe_name,
        },
      });
    } catch (error) {
      toast.error(error.message, {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <Dialog
      open={openScheduleModal}
      onClose={handleScheduleClose}
      PaperProps={{
        sx: {
          backgroundColor: theme.palette.background.paper,
          backdropFilter: "blur(10px)",
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
          overflow: 'visible',
          border: `1px solid ${theme.palette.divider}`,
        },
      }}
      maxWidth="sm"
      fullWidth
    >
      <ToastContainer />
      
      {/* Header with icon */}
      <Box
        sx={{
          position: 'relative',
          p: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          borderTopLeftRadius: 12,
          borderTopRightRadius: 12,
          color: 'white',
          textAlign: 'center',
        }}
      >
        <Avatar
          sx={{
            position: 'absolute',
            top: '-24px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 48,
            height: 48,
            backgroundColor: theme.palette.secondary.main,
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          }}
        >
          <RestaurantMenu />
        </Avatar>
        
        <IconButton
          onClick={handleScheduleClose}
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            color: 'white',
          }}
        >
          <CloseIcon />
        </IconButton>
        
        <Typography variant="h6" fontWeight="bold" mt={2}>
          Schedule Cooking
        </Typography>
      </Box>
      
      <DialogContent sx={{ p: 4, mt: 1 }}>
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="body1" 
            sx={{ 
              fontWeight: 500,
              color: theme.palette.text.primary 
            }}
          >
            When would you like to cook <Box component="span" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>{recipe_name}</Box>?
          </Typography>
        </Box>
        
        <Paper
          elevation={0}
          sx={{
            p: 2,
            borderRadius: 2,
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ScheduleIcon sx={{ mr: 2, color: theme.palette.primary.main }} />
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                label="Select Date and Time"
                value={scheduledDate}
                onChange={(newValue) => setScheduledDate(newValue)}
                sx={{
                  width: '100%',
                  '& .MuiInputBase-root': {
                    borderRadius: 2,
                  },
                  '& .MuiInputLabel-root.Mui-focused': {
                    color: theme.palette.primary.main,
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: theme.palette.primary.main,
                    borderWidth: 2,
                  },
                }}
              />
            </LocalizationProvider>
          </Box>
        </Paper>
      </DialogContent>
      
      <DialogActions
        sx={{
          justifyContent: 'space-between',
          p: 3,
          borderTop: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Button
          onClick={handleScheduleClose}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            color: theme.palette.text.secondary,
            '&:hover': {
              color: theme.palette.error.main,
            },
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleScheduleSave}
          variant="contained"
          disabled={isSubmitting}
          sx={{
            textTransform: 'none',
            fontWeight: 600,
            px: 4,
            py: 1,
            borderRadius: 2,
            background: `linear-gradient(45deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            boxShadow: '0 4px 10px rgba(0, 0, 0, 0.15)',
            '&:hover': {
              boxShadow: '0 6px 12px rgba(0, 0, 0, 0.2)',
            },
          }}
        >
          {isSubmitting ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            "Schedule Now"
          )}
        </Button>
      </DialogActions>
    </Dialog >
  );
}
